import React, { useState } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { 
  DashboardIcon, 
  ProductCatalogIcon, 
  FrameItForwardIcon, 
  OrdersIcon, 
  CustomersIcon, 
  PaymentsIcon, 
  ReportsIcon, 
  SettingsIcon, 
  LogoutIcon,
  LogoIcon,
  SearchIcon,
  NotificationIcon,
  MenuIcon,
  CloseIcon,
} from '../../../public/icons';

const AdminLayout = ({ children, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuSections = [
    {
      title: '',
      items: [
        { path: '/admin/dashboard', label: 'Dashboard', icon: DashboardIcon },
        { path: '/admin/memoir', label: 'Memoir', icon: ProductCatalogIcon },
        { path: '/admin/orders', label: 'Orders', icon: OrdersIcon },
        { path: '/admin/customers', label: 'Customers', icon: CustomersIcon },
        { path: '/admin/payment', label: 'Payments', icon: PaymentsIcon },
      ]
    },
    {
      title: '',
      items: [
        { path: '/admin/support', label: 'Reports', icon: ReportsIcon },
        { path: '/admin/blog', label: 'Blog', icon: SettingsIcon },
        { path: '/admin/settings', label: 'Settings', icon: SettingsIcon },
      ]
    },
  ];

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      navigate('/admin/login');
    }
  };

  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

  // Sidebar content component
  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="flex items-center justify-between px-4 sm:px-6 mt-6 sm:mt-10 mb-8 sm:mb-12">
        <LogoIcon color="#FCF7EE" size={28} />
        <button 
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden text-white hover:text-gray-300"
        >
          <CloseIcon size={24} color="#FCF7EE" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 sm:px-4">
        {/* First Section */}
        <div className="mb-4 sm:mb-6">
          {menuSections[0].items.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            const IconComponent = item.icon;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base font-normal transition-colors duration-200 mb-1 sm:mb-2 rounded-lg ${
                  isActive 
                    ? 'bg-[#162A55] text-[#F7F5EF] font-medium' 
                    : 'text-[#8A9099] hover:bg-[#162A55] hover:text-[#F7F5EF]'
                }`}
              >
                <IconComponent 
                  size={18} 
                  color={isActive ? '#F7F5EF' : '#8A9099'}
                />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
        
        {/* Second section */}
        <div className="mt-8 sm:mt-12 mb-2">
          {menuSections[1].items.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            const IconComponent = item.icon;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base font-normal transition-colors duration-200 mb-1 sm:mb-2 rounded-lg ${
                  isActive 
                    ? 'bg-[#162A55] text-[#F7F5EF] font-medium' 
                    : 'text-[#8A9099] hover:bg-[#162A55] hover:text-[#F7F5EF]'
                }`}
              >
                <IconComponent 
                  size={18} 
                  color={isActive ? '#F7F5EF' : '#8A9099'}
                />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Log Out Button */}
      <div className="p-3 sm:p-4 mt-auto border-t border-[#162A55]">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base font-medium rounded-lg transition-colors duration-200 text-[#8A9099] hover:bg-[#162A55] hover:text-[#F7F5EF]"
        >
          <LogoutIcon size={18} color="#8A9099" />
          <span className="font-medium">Log Out</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#f6f6f7]">
      {/* Mobile menu button */}
      <button
        onClick={() => setSidebarOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-30 p-2 "
      >
        <MenuIcon size={24} color="#0F1F3D" />
      </button>

      {/* Sidebar - Desktop always visible, Mobile conditional */}
      <div className={`
        fixed inset-y-0 left-0 z-40 w-64 sm:w-72 lg:w-80 bg-[#0F1F3D] transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <SidebarContent />
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="lg:ml-1 xl:ml-[15%] min-h-screen">
        {/* Top Bar */}
        <header className="bg-white sticky top-0 z-20">
          <div className="py-3 sm:py-4 md:py-5">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4 px-4 sm:px-6 md:px-8 lg:px-10 xl:px-32">
              <div className="text-left pl-12 md:pl-0">
                <h2 className="text-lg sm:text-xl md:text-2xl text-left font-medium text-[#0E1628]">
                  {menuSections.flatMap(s => s.items).find(item => location.pathname.startsWith(item.path))?.label || 'Admin Dashboard'} Overview
                </h2>
                <div className="text-xs sm:text-sm font-light text-[#6B635D] mt-0.5 sm:mt-1 text-left">
                  Welcome back! Here's what's happening with Kapta today.
                </div>
              </div>
              
              {/* User Info */}
              <div className="flex items-center justify-between md:justify-end gap-3 sm:gap-3.5">
                
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary flex items-center justify-center text-white font-bold text-sm sm:text-lg">
                  {adminUser.name?.[0] || 'A'}
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-gray-900 font-medium text-xs sm:text-sm">{adminUser.name || 'Admin User'}</p>
                  <p className="text-gray-500 text-xs">{adminUser.email || 'admin@kapta.co'}</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="pt-4 sm:pt-6 md:pt-8 lg:pt-10 px-4 sm:px-6 md:px-8 lg:px-10 xl:px-32 pb-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;